/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import CloseIcon from '@material-ui/icons/Close';

import Styled from './styles';
import ChartType from './components/chart-type';
import TimeRange from './components/time-range';
import Grouping from './components/grouping';
import Sorting from './components/sorting';
import Fields from './components/fields';
import { EditSnapPayload } from '../../store/Snaps/reducer';
import colors from '../../assets/colors';
import Modal from 'react-modal';
import MUIButton from '../mui-button';
import Description from './components/description';
import { editSnapDescription } from '../../store/Snaps/service';

interface IInputProps {
	data?: any;
	isLoading: boolean;
	type: string;
	toggleModal(): void;
	callback(payload: { data: EditSnapPayload }): void;
}

interface HookState {
	currentType: string;
	timeRange: any;
	grouping: any;
	sorting: any;
	fields: any;
	description: string;
}

const EditPopup: FC<IInputProps> = ({
	data,
	isLoading,
	type,
	toggleModal,
	callback,
}) => {
	moment.updateLocale('en', {
		week: {
			dow: 1,
		},
	});
	const [state, setState] = useState<HookState>({
		currentType: 'column',
		timeRange: null,
		grouping: null,
		sorting: null,
		fields: [],
		description: '',
	});

	useEffect(() => {
		setState(prev => ({
			...prev,
			currentType: type,
			grouping: data.grouping || {
				groupingLevel1: '',
				groupingLevel2: '',
			},
			timeRange: data.timeRange || {
				unit: '',
				range: '',
				from: null,
				to: null,
				field: '',
				groupingFields: [],
			},
			sorting: data.sorting || {
				field: '',
				order: '',
			},
			fields: data.fieldsToDisplay || [],
			description: data.description || '',
		}));
	}, [data]);

	const selectType = (currentType: string): void => {
		setState(prev => ({ ...prev, currentType }));
	};

	const selectRange = (range: any): void => {
		setState(prev => ({ ...prev, timeRange: { ...prev.timeRange, ...range } }));
	};

	const selectGroup = (grouping: any): void => {
		setState(prev => ({
			...prev,
			grouping: { ...prev.grouping, ...grouping },
		}));
	};

	const selectSort = (sorting: any): void => {
		setState(prev => ({
			...prev,
			sorting: { ...prev.sorting, ...sorting },
		}));
	};

	const selectField = (field: string): void => {
		setState(prev => ({
			...prev,
			fields: prev.fields.includes(field)
				? prev.fields.filter((el: string) => el !== field)
				: [...prev.fields, field],
		}));
	};

	const onChangeDescription = (description: string): void => {
		setState(prev => ({ ...prev, description }));
	};

	const onSave = (): void => {
		const { unit, range, field = '', groupingFields } = timeRange;
		const from = range
			? moment()
					.startOf(unit)
					.subtract(['last', 'this & last'].includes(range) ? 1 : 0, unit)
					.valueOf()
			: null;
		const to = range
			? moment()
					.endOf(unit)
					.subtract(['last'].includes(range) ? 1 : 0, unit)
					.valueOf()
			: null;
		callback({
			data: {
				id: data._id,
				chartType: currentType,
				grouping: {
					groupingLevel1: grouping.groupingLevel1,
					groupingLevel2: !grouping.groupingLevel1.length
						? ''
						: grouping.groupingLevel1 === grouping.groupingLevel2
						? 'NoGrouping'
						: grouping.groupingLevel2 || 'NoGrouping',
				},
				timeRange: {
					unit,
					range,
					from,
					to,
					field,
					groupingFields,
				},
				sorting: {
					field: sorting.field,
					order: sorting.order,
				},
				fields: state.fields,
			},
		});

		editSnapDescription(data._id, data.capturedInput, state.description);
	};

	const isCard = ['table'].includes(state.currentType);

	const tabsForCards = () => [
		{
			title: 'Chart type',
			Component: ChartType,
			value: 'currentType',
			fields: [],
			onPress: selectType,
		},
		{
			title: 'Time range',
			Component: TimeRange,
			value: 'timeRange',
			fields: data.timeRange.groupingFields,
			onPress: selectRange,
		},
		{
			title: 'Sorting',
			Component: Sorting,
			value: 'sorting',
			fields: data.groupingFields,
			onPress: selectSort,
		},
		{
			title: 'Fields',
			Component: Fields,
			value: 'fields',
			fields: data.groupingFields,
			onPress: selectField,
		},
		{
			title: 'Description',
			Component: Description,
			value: 'description',
			fields: state.description,
			onPress: onChangeDescription,
		},
	];

	const tabsForCommon = () => [
		{
			title: 'Chart type',
			Component: ChartType,
			value: 'currentType',
			fields: [],
			onPress: selectType,
		},
		{
			title: 'Time range',
			Component: TimeRange,
			value: 'timeRange',
			fields: [],
			onPress: selectRange,
		},
		{
			title: 'Grouping',
			Component: Grouping,
			value: 'grouping',
			fields: [...data.defaultFields, ...data.groupingFields],
			onPress: selectGroup,
		},
		{
			title: 'Description',
			Component: Description,
			value: 'description',
			fields: state.description,
			onPress: onChangeDescription,
		},
	];
	const tabs = useMemo(
		() => (isCard ? tabsForCards() : tabsForCommon()),
		[isCard]
	);

	const { currentType, grouping, timeRange, sorting } = state;

	return (
		<Modal isOpen={true} className="AdjustModal">
			<div>
				<Styled.Close onClick={toggleModal}>
					<CloseIcon style={{ color: colors.black }} />
				</Styled.Close>
			</div>
			<Styled.Container>
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<Styled.Title>Adjust widget</Styled.Title>
					<Styled.Content>
						{tabs.map(({ title, Component, value, fields, onPress }) => {
							return (
								<div style={{ marginTop: 8, width: '100%' }}>
									<Component
										title={title}
										value={state[value as keyof typeof state]}
										callback={onPress}
										fields={fields}
									/>
								</div>
							);
						})}
					</Styled.Content>
				</div>
				<div
					style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
				>
					<MUIButton title="Save settings" callBack={onSave} />
				</div>
			</Styled.Container>
		</Modal>
	);
};

export default EditPopup;
